import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  GoogleMap,
  Polygon,
  Circle,
  DrawingManager,
  Marker,
} from "@react-google-maps/api";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";

// MUI
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  Divider,
  Chip,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import MyLocationIcon from "@mui/icons-material/MyLocation";

const DEFAULT_CENTER = { lat: 28.4595, lng: 77.0266 };

const SHAPE_OPTIONS = {
  fillColor: "#3b82f6",
  fillOpacity: 0.3,
  strokeColor: "#2563eb",
  strokeWeight: 2,
  editable: true,
  draggable: true,
};

export default function GeofenceModal({
  selectedDeviceForGeofence,
  geofenceName,
  setGeofenceName,
  unsavedGeofence,
  setUnsavedGeofence,
  onGeofenceCreated,
  onGeofenceEdited,
  handleSaveGeofence,
  handleDeleteGeofence,
  setShowGeofenceModal,
  successMessage,
  errorMessage,
  getVehicleIcon,
}) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [mapsReady, setMapsReady] = useState(false);
  const [drawingMode, setDrawingMode] = useState(null);

  const mapRef = useRef(null);
  const shapeRef = useRef(null);

  // ✅ saved geofence exists?
  const hasSavedGeofence = useMemo(() => {
    return (
      Array.isArray(selectedDeviceForGeofence?.geofences) &&
      selectedDeviceForGeofence.geofences.length > 0
    );
  }, [selectedDeviceForGeofence]);

  // ✅ vehicle location
  const vehicleLocation = useCallback(() => {
    const loc = selectedDeviceForGeofence?.currentLocation;
    if (Number.isFinite(Number(loc?.latitude)) && Number.isFinite(Number(loc?.longitude))) {
      return {
        lat: Number(loc.latitude),
        lng: Number(loc.longitude),
      };
    }
    return DEFAULT_CENTER;
  }, [selectedDeviceForGeofence]);

  // ✅ load google maps ONCE
  useEffect(() => {
    let mounted = true;
    loadGoogleMaps().then(() => {
      if (mounted) setMapsReady(true);
    });
    return () => (mounted = false);
  }, []);

  // ✅ force resize on open (CRITICAL)
  useEffect(() => {
    if (!mapRef.current) return;

    const timer = setTimeout(() => {
      window.google?.maps?.event?.trigger(mapRef.current, "resize");
      mapRef.current.panTo(vehicleLocation());
      mapRef.current.setZoom(15);
    }, 200);

    return () => clearTimeout(timer);
  }, [mapsReady, vehicleLocation]);

  // ✅ sync edits
  const syncShape = useCallback(() => {
    if (!shapeRef.current || !unsavedGeofence) return;

    if (unsavedGeofence.type === "polygon") {
      const path = shapeRef.current.getPath();
      const coords = [];
      for (let i = 0; i < path.getLength(); i++) {
        coords.push({
          lat: path.getAt(i).lat(),
          lng: path.getAt(i).lng(),
        });
      }
      onGeofenceEdited({ type: "polygon", data: coords });
    }

    if (unsavedGeofence.type === "circle") {
      const center = shapeRef.current.getCenter();
      onGeofenceEdited({
        type: "circle",
        data: {
          center: { lat: center.lat(), lng: center.lng() },
          radius: shapeRef.current.getRadius(),
        },
      });
    }
  }, [unsavedGeofence, onGeofenceEdited]);

  // ✅ new shape drawn
  const onOverlayComplete = useCallback(
    (e) => {
      setDrawingMode(null);

      if (shapeRef.current) {
        shapeRef.current.setMap(null);
      }

      shapeRef.current = e.overlay;

      if (e.type === "polygon") {
        const path = e.overlay.getPath();
        const coords = [];
        for (let i = 0; i < path.getLength(); i++) {
          coords.push({
            lat: path.getAt(i).lat(),
            lng: path.getAt(i).lng(),
          });
        }
        onGeofenceCreated({ type: "polygon", data: coords });
      }

      if (e.type === "circle") {
        const center = e.overlay.getCenter();
        onGeofenceCreated({
          type: "circle",
          data: {
            center: { lat: center.lat(), lng: center.lng() },
            radius: e.overlay.getRadius(),
          },
        });
      }
    },
    [onGeofenceCreated],
  );

  const handleCenter = () => {
    if (!mapRef.current) return;
    mapRef.current.panTo(vehicleLocation());
    mapRef.current.setZoom(15);
  };

  const isSaveDisabled = !unsavedGeofence || !geofenceName?.trim();

  if (!mapsReady) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
        }}
      >
        <Paper sx={{ p: 3 }}>
          <Typography fontWeight={700}>Loading map…</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        p: 2,
      }}
    >
      <Paper sx={{ width: "min(980px, 100%)", borderRadius: 3, overflow: "hidden" }}>
        {/* HEADER */}
        <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <MapIcon color="primary" />
            <Typography fontWeight={800}>
              Geofence – {selectedDeviceForGeofence?.name}
            </Typography>
            {hasSavedGeofence && (
              <Chip size="small" color="success" label="Saved geofence exists" />
            )}
          </Stack>
          <IconButton onClick={() => setShowGeofenceModal(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* CONTROLS */}
        <Box sx={{ p: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            value={geofenceName}
            onChange={(e) => setGeofenceName(e.target.value)}
            size="small"
            fullWidth
            placeholder="Zone name"
          />

          <Button
            onClick={() => setDrawingMode("polygon")}
            variant={drawingMode === "polygon" ? "contained" : "outlined"}
            startIcon={<ChangeHistoryIcon />}
          >
            Polygon
          </Button>

          <Button
            onClick={() => setDrawingMode("circle")}
            variant={drawingMode === "circle" ? "contained" : "outlined"}
            startIcon={<RadioButtonUncheckedIcon />}
          >
            Circle
          </Button>

          <Button onClick={handleCenter} startIcon={<MyLocationIcon />}>
            Center
          </Button>
        </Box>

        <Alert severity="warning" sx={{ mx: 2 }}>
          Draw a polygon or circle around the vehicle, then save.
        </Alert>

        {/* MAP (FIXED HEIGHT – NO FLEX) */}
        <Box sx={{ p: 2 }}>
          <Paper
            sx={{
              height: isSm ? 380 : 520,
              width: "100%",
              overflow: "hidden",
            }}
          >
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: isSm ? "380px" : "520px",
              }}
              center={vehicleLocation()}
              zoom={15}
              onLoad={(map) => {
                mapRef.current = map;
                setTimeout(() => {
                  window.google.maps.event.trigger(map, "resize");
                  map.panTo(vehicleLocation());
                }, 200);
              }}
            >
              {selectedDeviceForGeofence?.currentLocation && (
                <Marker position={vehicleLocation()} />
              )}

              <DrawingManager
                drawingMode={drawingMode}
                onOverlayComplete={onOverlayComplete}
                options={{
                  drawingControl: false,
                  polygonOptions: SHAPE_OPTIONS,
                  circleOptions: SHAPE_OPTIONS,
                }}
              />

              {unsavedGeofence?.type === "polygon" && (
                <Polygon
                  paths={unsavedGeofence.data}
                  options={SHAPE_OPTIONS}
                  onLoad={(p) => (shapeRef.current = p)}
                  onMouseUp={syncShape}
                />
              )}

              {unsavedGeofence?.type === "circle" && (
                <Circle
                  center={unsavedGeofence.data.center}
                  radius={Number(unsavedGeofence.data.radius)}
                  options={SHAPE_OPTIONS}
                  onLoad={(c) => (shapeRef.current = c)}
                  onDragEnd={syncShape}
                />
              )}
            </GoogleMap>
          </Paper>
        </Box>

        <Divider />

        {/* FOOTER */}
        <Box sx={{ p: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={() => setShowGeofenceModal(false)}>Cancel</Button>

          {hasSavedGeofence && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDeleteGeofence}
            >
              Delete
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isSaveDisabled}
            onClick={handleSaveGeofence}
          >
            Save Geofence
          </Button>
        </Box>

        {(successMessage || errorMessage) && (
          <Box sx={{ p: 2 }}>
            {successMessage && <Alert severity="success">{successMessage}</Alert>}
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
